import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Table,
    Tag,
    Badge,
    Select,
    DatePicker,
    Input,
    Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import Header from "../../components/Header/header";
import styles from './audit.module.css'
import * as Action from "../../store/slices/audit/audit"
import * as UserAction from "../../store/slices/user/user"
import { connect } from "react-redux";
import dayjs from "dayjs";
import { message, Button } from "antd";
import { useNavigate } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getLoggedInUser } from "../../utils/auth/tokenValidation";

const { RangePicker } = DatePicker;


interface Api {
    getAudits: (payload: any) => unknown;
    Audit_Red: any;
    User_Red: any;
    getAllUser: (payload: any) => void;
}

interface UserActivityLog {
    id: number;
    timestamp: string;
    userName: string;
    email: string;
    role: string;
    module: string;
    action: string;
    result: "SUCCESS" | "FAILED";
    ipAddress: string;
}

// action tags use uppercase keys matching actionDescription values
const actionColor: Record<string, string> = {
    LOGIN: "blue",
    LOGOUT: "default",
    CREATE: "green",
    UPDATE: "cyan",
    DELETE: "red",
    ACCESS: "purple",
};

const UserActivityAuditLog: React.FC<Api> = ({ getAudits, Audit_Red, User_Red, getAllUser }) => {
    const navigate = useNavigate();
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [loggedInUser] = useState(() => getLoggedInUser());
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [filters, setFilters] = useState<{
        dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
        page: number;
        size: number;
        username: string;
        userId?: number | null;

    }>({
        dateRange: [dayjs().subtract(30, "day"), dayjs()],
        page: 1,
        size: 10,
        username: "",
        userId: loggedInUser.userId, // API always uses logged-in user by default

    });


    useEffect(() => {
        getAllUser({ page: 0, size: 1000 });
    }, [getAllUser]);



    const userOptions = User_Red?.data?.content?.map((user: any) => ({
            label: user.userName || user.name,
            value: user.userId,
        })) || [];




    const getFormattedDates = () => {
        if (!filters.dateRange?.[0] || !filters.dateRange?.[1]) return {};
        return {
            fromDate: filters.dateRange[0].format("YYYY-MM-DD"),
            toDate: filters.dateRange[1].format("YYYY-MM-DD"),
        };
    };

    const fetchAudits = useCallback(() => {
        const { fromDate, toDate } = getFormattedDates();

        getAudits({
            page: filters.page,
            size: filters.size,
            fromDate,
            toDate,
            userId: filters.userId ?? 0,
            username: filters.username.trim(),
            ipAddress: "",
            actionName: "",
            isException: false,
            sortBy: "requestAt",
            direction: "DESC",
        });
    }, [filters, getAudits]);

    useEffect(() => {
        fetchAudits();
    }, [fetchAudits]);


    const pageData = Audit_Red?.data?.[0]?.res?.data;

    // Security Policy: Check for status codes 1004 and 1005
    useEffect(() => {
        if (Audit_Red?.dataSingle?.[0]?.res?.status === '1004') {
            message.error(Audit_Red.dataSingle[0].res.message);
            localStorage.clear();
            navigate('/');
        } else if (Audit_Red?.dataSingle?.[0]?.res?.status === '1005') {
            message.warning(Audit_Red.dataSingle[0].res.message);
        }
    }, [Audit_Red, navigate]);

    const tableData: UserActivityLog[] =
        pageData?.content?.map((item: any) => ({
            id: item.activityLogId,
            timestamp: item.requestAt,
            userName: item.username || "-",
            email: "-",
            role: "-",
            module: item.module || item.module || "-",
            action: item.actionDescription || "-",
            result: item.exceptionMsg ? "FAILED" : "SUCCESS",
            ipAddress: item.ipAddress || "-",
        })) ?? [];




    const columns: ColumnsType<UserActivityLog> = [
        {
            title: "Date & Time",
            dataIndex: "timestamp",
            key: "timestamp",
            render: (text) => (text ? dayjs(text).format("YYYY-MM-DD HH:mm:ss") : "-"),
        },
        {
            title: "User",
            key: "user",
            render: (_, record) => (
                <>
                    <strong>{record.userName}</strong>
                    <br />
                    {/* <small>{record.email}</small> */}
                </>
            ),
        },
        {
            title: "Module",
            dataIndex: "module",
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (action) => (
                <Tag color={actionColor[action?.toUpperCase()] || "default"}>{action}</Tag>
            ),
        },
        {
            title: "Result",
            dataIndex: "result",
            render: (result) => (
                <Badge status={result === "SUCCESS" ? "success" : "error"} text={result} />
            ),
        },
        {
            title: "IP Address",
            dataIndex: "ipAddress",
        },
    ];






    const handleTableChange = (pagination: any) => {
        setFilters((prev) => ({
            ...prev,
            page: pagination.current - 1,
            size: pagination.pageSize,
        }));
    };

    const handleDateChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        setFilters((prev) => ({
            ...prev,
            dateRange: dates,
            page: 0,
        }));
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters((prev) => ({ ...prev, username: e.target.value, page: 0 }));
    };

    const total = pageData?.totalElements ?? 0;

    const generatePDF = async () => {
        if (total === 0) {
            message.warning('No data available to generate report');
            return;
        }
        
        setIsGeneratingPDF(true);
        const hideLoading = message.loading('Fetching all records for PDF...', 0);
        
        try {
            const { fromDate, toDate } = getFormattedDates();
            const selectedUser = userOptions.find((u: any) => u.value === filters.userId);
            
            const token = localStorage.getItem("loginToken");
            const API_URL = process.env.REACT_APP_API_URL;
            
            const query = new URLSearchParams({
                fromDate: fromDate || '',
                toDate: toDate || '',
                userId: String(filters.userId || 0),
                username: filters.username.trim(),
                ipAddress: "",
                actionName: "",
                isException: "false",
                page: "0",
                size: "100000",
                sortBy: "requestAt",
                direction: "DESC",
            }).toString();
            
            const response = await fetch(`${API_URL}/api/activity/logs?${query}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            
            const result = await response.json();
            const fullData = result?.data?.content || [];
            
            if (fullData.length === 0) {
                hideLoading();
                message.warning('No data retrieved for PDF');
                setIsGeneratingPDF(false);
                return;
            }
            
            const doc = new jsPDF();
            doc.setFontSize(18);
            doc.text('Audit Activity Report', 14, 20);
            
            doc.setFontSize(11);
            doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, 14, 30);
            doc.text(`Period: ${fromDate || 'N/A'} to ${toDate || 'N/A'}`, 14, 37);
            doc.text(`User: ${selectedUser ? selectedUser.label : 'All Users'}`, 14, 44);
            doc.text(`Total Records: ${fullData.length}`, 14, 51);
            
            const tableColumn = ['Date & Time', 'User', 'Module', 'Action', 'Result', 'IP Address'];
            const tableRows = fullData.map((item: any) => [
                dayjs(item.requestAt).format('YYYY-MM-DD HH:mm:ss'),
                item.username || '-',
                item.uri || item.module || '-',
                item.actionName || '-',
                item.exceptionMsg ? 'FAILED' : 'SUCCESS',
                item.ipAddress || '-'
            ]);
            
            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 58,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185] }
            });
            
            const fileName = `audit_report_${selectedUser ? selectedUser.label : 'all_users'}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`;
            doc.save(fileName);
            hideLoading();
            message.success(`PDF report with ${fullData.length} records downloaded successfully`);
            setIsGeneratingPDF(false);
        } catch (error) {
            hideLoading();
            message.error('Failed to generate PDF report');
            setIsGeneratingPDF(false);
        }
    };


    return (
        <>
            <Header />
            <div className={styles.container}>
                <div>
                    <h4>Activity Audit Log</h4>
                </div>
                <Card title="User Activity Audit Log" bordered={false}>
                    {/* Filters */}
                    <Space wrap style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
                        <Space wrap>
                        <Select
                            placeholder="User"
                            style={{ width: 160 }}
                            allowClear
                            options={userOptions}
                            value={selectedUserId ?? undefined}
                            onChange={(value) => {
                                setSelectedUserId(value ?? null);
                                setFilters((prev) => ({
                                    ...prev,
                                    userId: value ?? loggedInUser.userId,
                                    page: 0,
                                }));
                            }}
                        />
                        {/* <Select placeholder="Module" style={{ width: 160 }} />
                        <Select placeholder="Action" style={{ width: 160 }} />
                        <Select placeholder="Result" style={{ width: 140 }} /> */}
                        <RangePicker
                            value={filters.dateRange}
                            onChange={handleDateChange}
                            format="YYYY-MM-DD"
                            allowClear={false} // prevents null range issues
                            style={{ width: 240 }}
                        />
                        <Input.Search
                            placeholder="Search by username..."
                            allowClear
                            style={{ width: 220 }}
                            value={filters.username}
                            onChange={handleUsernameChange}
                        // onSearch not needed anymore with live filtering
                        />
                        </Space>
                        <Button 
                            type="primary" 
                            icon={<DownloadOutlined />}
                            onClick={generatePDF}
                            disabled={total === 0 || isGeneratingPDF}
                            loading={isGeneratingPDF}
                        >
                            Export Data
                        </Button>
                    </Space>

                    {/* Table */}
                    <Table
                        columns={columns}
                        rowKey="id"
                        dataSource={tableData}
                        loading={Audit_Red.loading}
                        onChange={handleTableChange}
                        pagination={{
                            current: filters.page + 1,
                            pageSize: filters.size,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "15", "20", "50"],
                            showTotal: (tot) => `Total ${tot} records`,
                        }}
                    />
                </Card>

            </div>
        </>
    );
}

function mapStateToProps(state: any) {
    return {
        Audit_Red: state.Audit_Red,
        User_Red: state.User_Red
    }
}


export default connect(mapStateToProps, { ...Action, ...UserAction })(UserActivityAuditLog)
